import { useState } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { getPlans, getAlaCarteItems, getPlan } from '../utils/monetization';

function CheckIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>;
}

function PlanCard({ plan, current, onSelect, highlight }) {
  const tr = useTr();
  const PLANS = getPlans(lang);
  const ALA_CARTE = getAlaCarteItems(lang);
  const isActive = current?.id === plan.id;
  const { subsEnabled } = useApp();
  if (!subsEnabled) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem', textAlign:'center' }}>
        <div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'3rem', color:'var(--gold)', marginBottom:'1rem', lineHeight:1 }}>✦</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:400, color:'var(--white)', marginBottom:'0.5rem' }}>
            {tr('upgrade.launch_free')}
          </h2>
          <p style={{ fontSize:'0.85rem', color:'var(--muted)', lineHeight:1.7, maxWidth:320, margin:'0 auto' }}>
            {tr('upgrade.launch_free_sub')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: highlight ? 'var(--gold-pale)' : 'var(--dark)', border:`1px solid ${highlight ? 'var(--gold)' : isActive ? 'var(--gold-border)' : 'var(--border)'}`, borderRadius:'var(--r-xl)', padding:'1.3rem 1.2rem', position:'relative', overflow:'hidden' }}>
      {highlight && (
        <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg, transparent, var(--gold), transparent)' }}/>
      )}
      {highlight && (
        <div style={{ position:'absolute', top:10, right:12, background:'var(--gold)', color:'var(--black)', fontSize:'0.55rem', fontWeight:700, padding:'0.12rem 0.5rem', borderRadius:99, fontFamily:'Outfit', letterSpacing:'0.06em' }}>{tr('upgrade.most_popular')}</div>
      )}
      <div style={{ display:'flex', alignItems:'baseline', gap:'0.4rem', marginBottom:'0.3rem' }}>
        {plan.badge && <span style={{ fontSize:'1.2rem' }}>{plan.badge}</span>}
        <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:500, color:'var(--white)' }}>{plan.name}</span>
      </div>
      {plan.tagline && <p style={{ fontSize:'0.68rem', color:'var(--muted)', marginBottom:'0.8rem' }}>{plan.tagline}</p>}
      <div style={{ marginBottom:'1rem' }}>
        {plan.price === 0
          ? <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2rem', color:'var(--white)' }}>{tr('upgrade.free_label2')}</span>
          : <><span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2rem', color:'var(--gold)', fontWeight:600 }}>${plan.price}</span><span style={{ fontSize:'0.72rem', color:'var(--muted)' }}> CAD/mo</span></>
        }
        {plan.trialDays && <div style={{ fontSize:'0.62rem', color:'var(--gold)', marginTop:'0.15rem' }}>{plan.trialDays}-day free trial</div>}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:'0.45rem', marginBottom:'1.2rem' }}>
        {plan.features.map((f,i) => (
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'0.4rem' }}>
            <div style={{ flexShrink:0, marginTop:1 }}><CheckIcon/></div>
            <span style={{ fontSize:'0.75rem', color:'var(--white)', lineHeight:1.4 }}>{f}</span>
          </div>
        ))}
      </div>
      {isActive ? (
        <div style={{ width:'100%', padding:'0.6rem', background:'rgba(255,255,255,0.05)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.72rem', fontWeight:600, textAlign:'center', letterSpacing:'0.06em' }}>
          ✓ Current plan
        </div>
      ) : (
        <button onClick={() => onSelect(plan)} style={{ width:'100%', padding:'0.7rem', background: highlight ? 'var(--gold)' : 'transparent', border:`1px solid ${highlight ? 'var(--gold)' : 'var(--gold-border)'}`, borderRadius:'var(--r-md)', color: highlight ? 'var(--black)' : 'var(--gold)', fontFamily:'Outfit', fontSize:'0.75rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.08em' }}>
          {plan.price === 0 ? tr('upgrade.stay_free') : `Start ${plan.trialDays ? plan.trialDays+'-day trial' : 'now'} →`}
        </button>
      )}
    </div>
  );
}

export default function Upgrade({ onClose, initialTab }) {
  const tr = useTr();
  const PLANS = getPlans(lang);
  const ALA_CARTE = getAlaCarteItems(lang);
  const { currentUser, setCurrentUser, updateSubscription } = useApp();
  const [tab, setTab] = useState(initialTab || 'plans');
  const [billing, setBilling] = useState('monthly');
  const [success, setSuccess] = useState(null);
  const current = getPlan(currentUser);

  const handleSelectPlan = async (plan) => {
    if (plan.id === 'free') { setSuccess('downgrade'); return; }
    // In production: go through Stripe subscription flow
    // subscribeToPlan({ priceId: plan.stripePrice, userId: currentUser.id })
    // then call updateSubscription() on success callback from StripeCheckout
    // For now: optimistic update (Stripe webhook will confirm)
    const expiresAt = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();
    await updateSubscription(plan.id, expiresAt);
    setSuccess(plan.id);
  };

  const handleBuyAddon = async (addon) => {
    // À-la-carte items: create one-time payment via Stripe
    // payForEvent({ event: { id: addon.id, price: addon.price }, user: currentUser })
    setSuccess('addon_' + addon.id);
  };

  if (success) {
    const plan = PLANS[success] || null;
    return (
      <div style={{ padding:'3rem 1.5rem', textAlign:'center', maxWidth:340, margin:'0 auto' }}>
        <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>{plan?.badge || '✦'}</div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', color:'var(--white)', marginBottom:'0.5rem' }}>
          {success === 'downgrade' ? tr('upgrade.back_free') : success.startsWith('addon_') ? 'Add-on activated!' : `Welcome to ${plan?.name}!`}
        </h2>
        <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.6, marginBottom:'1.5rem' }}>
          {success === 'downgrade' ? tr('upgrade.plan_active') : success.startsWith('addon_') ? 'Your add-on is now active.' : `Your ${plan?.trialDays}-day free trial has started. No charge until then.`}
        </p>
        <button onClick={onClose} style={{ padding:'0.7rem 2rem', background:'var(--gold)', border:'none', borderRadius:'var(--r-md)', color:'var(--black)', fontFamily:'Outfit', fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
          {onClose ? tr('upgrade.done') : tr('upgrade.back_profile')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:480, margin:'0 auto', paddingBottom:'2rem' }}>
      {/* Header */}
      <div style={{ padding:'1.5rem 1.2rem 0.8rem', textAlign:'center' }}>
        <div style={{ height:2, background:'linear-gradient(90deg,transparent,var(--gold),transparent)', marginBottom:'1.2rem' }}/>
        <p style={{ fontSize:'0.6rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'var(--gold)', fontFamily:'Outfit', marginBottom:'0.3rem' }}>{tr('upgrade.premium_title')}</p>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.7rem', fontWeight:400, color:'var(--white)', marginBottom:'0.3rem', lineHeight:1.2 }}>
          Upgrade your<br/><em style={{ color:'var(--gold)' }}>{tr('upgrade.dating_game')}</em>
        </h1>
        <p style={{ fontSize:'0.75rem', color:'var(--muted)' }}>{tr('upgrade.no_fees')}</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', margin:'0.8rem 1.2rem 0' }}>
        {[['plans','Plans'],['addons','Add-ons']].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{ padding:'0.55rem 1rem', fontSize:'0.65rem', letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:'Outfit', fontWeight:600, border:'none', background:'transparent', cursor:'pointer', color:tab===id?'var(--gold)':'var(--muted)', borderBottom:tab===id?'2px solid var(--gold)':'2px solid transparent' }}>{label}</button>
        ))}
      </div>

      {/* Plans */}
      {tab === 'plans' && (
        <div style={{ padding:'1rem 1.2rem', display:'flex', flexDirection:'column', gap:'1rem' }}>
          {[PLANS.free, PLANS.honey, PLANS.gold].map(plan => (
            <PlanCard key={plan.id} plan={plan} current={current} onSelect={handleSelectPlan} highlight={plan.id==='honey'}/>
          ))}
        </div>
      )}

      {/* Add-ons */}
      {tab === 'addons' && (
        <div style={{ padding:'1rem 1.2rem', display:'flex', flexDirection:'column', gap:'0.7rem' }}>
          <p style={{ fontSize:'0.72rem', color:'var(--muted)', marginBottom:'0.3rem' }}>{tr('upgrade.one_time')}</p>
          {ALA_CARTE.map(addon => {
            const owned = (currentUser?.subscription?.addons||[]).includes(addon.id);
            return (
              <div key={addon.id} style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem 1.1rem', display:'flex', alignItems:'center', gap:'0.9rem' }}>
                <span style={{ fontSize:'1.5rem', flexShrink:0 }}>{addon.icon}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.15rem' }}>
                    <span style={{ fontSize:'0.8rem', fontWeight:600, color:'var(--white)', fontFamily:'Outfit' }}>{addon.name}</span>
                    {addon.badge && <span style={{ fontSize:'0.52rem', background:'rgba(78,201,148,0.15)', color:'#4EC994', padding:'0.06rem 0.35rem', borderRadius:99, fontFamily:'Outfit', fontWeight:600 }}>{addon.badge}</span>}
                  </div>
                  <p style={{ fontSize:'0.65rem', color:'var(--muted)' }}>{addon.desc}</p>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', color:'var(--gold)', fontWeight:600, lineHeight:1 }}>${addon.price}</div>
                  {addon.period && <div style={{ fontSize:'0.55rem', color:'var(--muted)' }}>/{addon.period}</div>}
                  {addon.once && <div style={{ fontSize:'0.55rem', color:'var(--muted)' }}>{tr('upgrade.one_time')}</div>}
                  <button onClick={()=>handleBuyAddon(addon)} disabled={owned} style={{ marginTop:'0.4rem', padding:'0.25rem 0.65rem', background: owned ? 'transparent' : 'var(--gold-pale)', border:`1px solid ${owned?'var(--border)':'var(--gold-border)'}`, borderRadius:'var(--r-pill)', color: owned ? 'var(--muted)' : 'var(--gold)', fontFamily:'Outfit', fontSize:'0.6rem', fontWeight:600, cursor: owned ? 'default':'pointer' }}>
                    {owned ? '✓ Active' : 'Add'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
